import React, { useEffect, useRef } from 'react';

export type MemberTabId = 'overview' | 'timeline' | 'docs' | 'observations';

interface TabDef {
  id: MemberTabId;
  label: string;
  /** Optional badge text/number (rendered if not null/undefined). */
  badge?: number | string | null;
}

interface MemberTabsProps {
  active: MemberTabId;
  onChange: (id: MemberTabId) => void;
  /** Counts come from MemberDetail and drive the tab badges. */
  timelineCount?: number | null;
  docCount?: number | null;
  /** When true, the "Observations" tab uses the advanced label. */
  isAdvanced: boolean;
}

const ORDER: MemberTabId[] = ['overview', 'timeline', 'docs', 'observations'];

const MemberTabs: React.FC<MemberTabsProps> = ({
  active,
  onChange,
  timelineCount,
  docCount,
  isAdvanced,
}) => {
  const tabsRef = useRef<Record<MemberTabId, HTMLButtonElement | null>>({
    overview: null,
    timeline: null,
    docs: null,
    observations: null,
  });

  const tabs: TabDef[] = [
    { id: 'overview', label: 'Prezentare generală' },
    { id: 'timeline', label: 'Cronologie', badge: timelineCount ?? undefined },
    { id: 'docs', label: 'Documente', badge: docCount ?? undefined },
    {
      id: 'observations',
      label: isAdvanced ? 'Analiza & Insight-uri' : 'Observații',
    },
  ];

  // Keyboard nav: ArrowLeft/Right to move; Home/End to jump.
  const onKeyDown = (ev: React.KeyboardEvent<HTMLButtonElement>) => {
    const idx = ORDER.indexOf(active);
    if (idx < 0) return;
    let next: number | null = null;
    switch (ev.key) {
      case 'ArrowRight':
        next = (idx + 1) % ORDER.length;
        break;
      case 'ArrowLeft':
        next = (idx - 1 + ORDER.length) % ORDER.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = ORDER.length - 1;
        break;
      default:
        return;
    }
    ev.preventDefault();
    const nextId = ORDER[next!];
    onChange(nextId);
    // Focus the new tab after React re-renders.
    window.requestAnimationFrame(() => {
      tabsRef.current[nextId]?.focus();
    });
  };

  // Scroll active tab into view on mobile (horizontal overflow).
  useEffect(() => {
    tabsRef.current[active]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [active]);

  return (
    <div className="member-tabs-bar" role="tablist" aria-label="Secțiuni membru">
      <div className="tabs-bar member-tabs-bar-inner">
        {tabs.map((tab) => {
          const selected = tab.id === active;
          const showBadge = tab.badge !== undefined && tab.badge !== null && tab.badge !== '';
          return (
            <button
              key={tab.id}
              ref={(el) => {
                tabsRef.current[tab.id] = el;
              }}
              type="button"
              role="tab"
              id={`member-tab-${tab.id}`}
              aria-controls={`member-tabpanel-${tab.id}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              className="tabs-bar__tab member-tab"
              onClick={() => onChange(tab.id)}
              onKeyDown={onKeyDown}
            >
              <span>{tab.label}</span>
              {showBadge && <span className="tabs-bar__count member-tab-badge">{tab.badge}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MemberTabs;
